import { api } from './api-client';
import { cached, invalidatePrefix } from './api-cache';
import type { TicketOptionSourceType } from './ticket-field-registry';

export interface TicketFormFieldConfig {
  fieldKey: string;
  position: number;
  enabled: boolean;
  requiredOverride: boolean | null;
  visibleToCustomers: boolean;
  optionSourceType?: TicketOptionSourceType;
  optionSourceId?: string | null;
}

// ponytail: request shape for PUT .../fields — every property but the three
// required ones is optional to match the backend DTO. Omitting a property
// that equals its computed default (see ticket-forms.service.ts
// validateFields/normalizeFields, which falls back to the same defaults)
// keeps large form-field-update payloads under the ALB WAF's fixed 8KB
// body-inspection limit. Confirmed via ALB logs: requestBodySize 9471 >
// requestBodySizeInspectedByWAF 8192. Ceiling: trims per-field defaults, not
// field count — a form with enough non-default overrides could still exceed 8KB.
export type TicketFormFieldUpdate = Pick<TicketFormFieldConfig, 'fieldKey' | 'position' | 'enabled'>
  & Partial<Pick<TicketFormFieldConfig, 'requiredOverride' | 'visibleToCustomers' | 'optionSourceType' | 'optionSourceId'>>;

export interface TicketFormFieldDefinition extends TicketFormFieldConfig {
  id: string;
  label: string;
  description: string | null;
  fieldType: string;
  rendererType: string;
  labelForCustomers: string | null;
  requiredForAgents: boolean;
  requiredForCustomers: boolean;
  displayedToCustomers: boolean;
  customersCanEdit: boolean;
  customersCanFilter: boolean;
  isDefault: boolean;
  isActive: boolean;
  choices: unknown;
  dependsOnFieldKey: string | null;
}

export interface TicketFormRuleCondition {
  fieldKey: string;
  operator: 'equals' | 'in' | 'empty';
  value?: unknown;
}

export interface TicketFormRule {
  logic: 'AND' | 'OR';
  conditions: TicketFormRuleCondition[];
  action: 'show' | 'hide' | 'required' | 'optional' | 'value' | 'readonly';
  targetFieldKey: string;
  actionValue?: unknown;
  position: number;
  isActive: boolean;
}

export interface TicketForm {
  id: string;
  name: string;
  description: string | null;
  fields: TicketFormFieldConfig[];
  fieldDefinitions: TicketFormFieldDefinition[];
  rules?: TicketFormRule[];
  isDefault: boolean;
  isPublished: boolean;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTicketFormInput {
  name: string;
  description?: string;
  fields?: TicketFormFieldConfig[];
  rules?: TicketFormRule[];
  isDefault?: boolean;
  isPublished?: boolean;
}

export type UpdateTicketFormInput = Partial<CreateTicketFormInput>;

interface TicketFormResponse {
  data: TicketForm;
}

interface TicketFormsResponse {
  data: TicketForm[];
}

export async function getTicketForms(): Promise<TicketForm[]> {
  return cached('ticket-forms', async () => {
    const response = await api.get<TicketFormsResponse>('/admin/ticket-forms');
    return response.data;
  });
}

export async function getTicketForm(id: string): Promise<TicketForm> {
  return cached(`ticket-form:${id}`, async () => {
    const response = await api.get<TicketFormResponse>(`/admin/ticket-forms/${encodeURIComponent(id)}`);
    return response.data;
  });
}

export async function createTicketForm(input: CreateTicketFormInput): Promise<TicketForm> {
  const response = await api.post<TicketFormResponse>('/admin/ticket-forms', input);
  invalidatePrefix('ticket-form');
  return response.data;
}

export async function updateTicketForm(id: string, input: UpdateTicketFormInput): Promise<TicketForm> {
  const response = await api.put<TicketFormResponse>(`/admin/ticket-forms/${encodeURIComponent(id)}`, input);
  invalidatePrefix('ticket-form');
  return response.data;
}

export async function updateTicketFormFields(
  id: string,
  fields: TicketFormFieldUpdate[],
): Promise<TicketForm> {
  const response = await api.put<TicketFormResponse>(
    `/admin/ticket-forms/${encodeURIComponent(id)}/fields`,
    { fields },
  );
  invalidatePrefix('ticket-form');
  return response.data;
}

export async function deleteTicketForm(id: string): Promise<void> {
  await api.delete(`/admin/ticket-forms/${encodeURIComponent(id)}`);
  invalidatePrefix('ticket-form');
}

export async function publishTicketForm(id: string): Promise<TicketForm> {
  const response = await api.post<TicketFormResponse>(`/admin/ticket-forms/${encodeURIComponent(id)}/publish`);
  invalidatePrefix('ticket-form');
  return response.data;
}

export async function unpublishTicketForm(id: string): Promise<TicketForm> {
  const response = await api.post<TicketFormResponse>(`/admin/ticket-forms/${encodeURIComponent(id)}/unpublish`);
  invalidatePrefix('ticket-form');
  return response.data;
}
